import React from 'react'
import {
  Box,
  IconButton,
  Typography,
  Modal,
  Button,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Skeleton,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
} from '@mui/material'
import {
  Close,
  ArrowBack,
  CameraAlt,
  FlipCameraIos,
  RestartAlt,
  QrCode2,
  Delete,
  CheckCircle,
  Error,
} from '@mui/icons-material'
import SPDCortexScanner from 'views/SPDLayout/SPD/SPDCortexScanner/SPDCortexScanner'
import { LoadingButton } from 'components/mui'
import dayjs from 'lib/dayjs'

import { useUDIScannerModalLogic } from './UDIScannerModal.logic'
import { UDIScannerModalProps } from './UDIScannerModal.types'
import './UDIScannerModal.scss'

const UDIScannerModal = ({
  open,
  onClose,
  onScan,
  onTrayStateUpdate,
  searchResult,
  isSearching,
  searchError,
  referenceImageUrl,
  trayType,
}: UDIScannerModalProps) => {
  const {
    currentMode,
    scanMode,
    scannedProduct,
    continuousScannedProducts,
    quantity,
    isProcessingScan,
    handleUDIScan,
    handleScanModeToggle,
    handleRemoveScannedProduct,
    handleFinishScanning,
    videoRef,
    cameras,
    activeCamera,
    isCameraMenuOpen,
    image,
    handleSelectCamera,
    handleToggleCameraMenu,
    handleImageCapture,
    handleProceedToCamera,
    handleConfirmImage,
    handleRetakeImage,
    handleBackToScanning,
    handleQuantityChange,
    handleClose,
  } = useUDIScannerModalLogic({
    open,
    onClose,
    onScan,
    onTrayStateUpdate,
    searchResult,
    isSearching,
    searchError,
    referenceImageUrl,
    trayType,
  })
  
  const renderScanningMode = () => (
    <>
      <Box className="modal-header">
        <Typography variant="h5">Scan Product UDI</Typography>
        <IconButton onClick={handleClose} className="close-button">
          <Close />
        </IconButton>
      </Box>

      <Box className="scan-mode-toggle">
        <FormControlLabel
          control={
            <Switch
              checked={scanMode === 'continuous'}
              onChange={handleScanModeToggle}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">Continuous AI Capture Mode</Typography>
          }
        />
      </Box>

      <Typography variant="body1" className="scanner-instruction">
        Position the product's UDI barcode within the viewfinder to scan
      </Typography>

      <Box className="scanner-viewfinder">
        <SPDCortexScanner
          canScan={open && currentMode === 'scanning'}
          onScan={handleUDIScan}
        />
        <Skeleton
          className="skeleton"
          variant="rounded"
          width="100%"
          height="100%"
          animation="wave"
        />
        {(isSearching || isProcessingScan) && (
          <Box className="loading-overlay">
            <CircularProgress size="0.8em" className="spinner" />
            <Typography variant="body1">
              {isProcessingScan
                ? 'Processing scan...'
                : 'Searching for product...'}
            </Typography>
          </Box>
        )}
      </Box>

      {searchError && !isSearching && (
        <Typography variant="body2" className="error">
          {searchError.message}
        </Typography>
      )}
    </>
  )

  const renderContinuousScanningMode = () => (
    <Box className="continuous-scanning-container">
      <Box className="modal-header">
        <Typography variant="h5">Continuous UDI Scanning</Typography>
        <IconButton onClick={handleClose} className="close-button">
          <Close />
        </IconButton>
      </Box>

      <Box className="scan-mode-toggle">
        <FormControlLabel
          control={
            <Switch
              checked={scanMode === 'continuous'}
              onChange={handleScanModeToggle}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">Continuous AI Capture Mode</Typography>
          }
        />
      </Box>

      <Box className="continuous-content">
        <Box className="scanner-section">
          <Typography variant="body1" className="scanner-instruction">
            <p>Scan multiple products. Each scan will be added to the list.</p>
            <p>Then load all hardware into the tray and capture tray image.</p>
          </Typography>

          <Box className="scanner-viewfinder">
            <SPDCortexScanner
              canScan={open && currentMode === 'continuous-scanning'}
              onScan={handleUDIScan}
            />
            <Skeleton
              className="skeleton"
              variant="rounded"
              width="100%"
              height="100%"
              animation="wave"
            />
            {(isSearching || isProcessingScan) && (
              <Box className="loading-overlay">
                <CircularProgress size="0.8em" className="spinner" />
                <Typography variant="body1">
                  {isProcessingScan
                    ? 'Processing scan...'
                    : 'Searching for product...'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box className="scanned-products-section">
          <Box className="scanned-products-header">
            <Typography variant="h6">
              Scanned Products ({continuousScannedProducts.length})
            </Typography>
            {continuousScannedProducts.length > 0 && (
              <Chip
                label={`Total Qty: ${continuousScannedProducts
                  .filter((p) => !p.error)
                  .reduce((sum, p) => sum + (p.quantity || 1), 0)}`}
                color="primary"
                size="small"
              />
            )}
          </Box>

          <Box className="scanned-products-list">
            {continuousScannedProducts.length === 0 ? (
              <Box className="empty-state">
                <QrCode2
                  sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  No products scanned yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Start scanning to add products to this list
                </Typography>
              </Box>
            ) : (
              <List className="products-list">
                {continuousScannedProducts.map((product, index) => (
                  <React.Fragment key={product.id}>
                    <ListItem
                      className={`product-item ${
                        product.error ? 'error' : 'success'
                      }`}
                    >
                      <Box className="product-icon">
                        {product.error ? (
                          <Error color="error" />
                        ) : (
                          <CheckCircle color="success" />
                        )}
                      </Box>
                      <ListItemText
                        primary={
                          <Box className="product-primary">
                            <Typography
                              variant="subtitle2"
                              className="product-name"
                            >
                              {product.productName}
                            </Typography>
                            {product.quantity && product.quantity > 1 && (
                              <Chip
                                label={`Qty: ${product.quantity}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box className="product-secondary">
                            {product.error ? (
                              <Typography variant="caption" color="error">
                                {product.error}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" display="block">
                                  {product.manufacturer}
                                </Typography>
                                {product.screwSize && (
                                  <Typography variant="caption" display="block">
                                    Size: {product.screwSize}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block">
                                  ID: {product.deviceId}
                                </Typography>
                                {product.lotNumber && (
                                  <Typography variant="caption" display="block">
                                    Lot: {product.lotNumber}
                                  </Typography>
                                )}
                              </>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Scanned:{' '}
                              {dayjs(product.scannedAt).format('HH:mm:ss')}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveScannedProduct(product.id)}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < continuousScannedProducts.length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {continuousScannedProducts.length > 0 && (
            <Box className="finish-scanning-section">
              <LoadingButton
                variant="contained"
                onClick={handleFinishScanning}
                className="finish-scanning-btn"
                startIcon={<CameraAlt />}
                fullWidth
              >
                Capture Tray Image
              </LoadingButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )

  const renderProductDetailsMode = () => (
    <>
      <Box className="modal-header">
        <IconButton onClick={handleBackToScanning} className="back-button">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">Product Found</Typography>
        <IconButton onClick={handleClose} className="close-button">
          <Close />
        </IconButton>
      </Box>

      <Card className="product-details-card">
        <CardContent>
          <Box className="product-info">
            <Box className="product-header">
              <QrCode2 className="success-icon" />
              <Typography variant="h6">Product Successfully Scanned</Typography>
            </Box>

            {scannedProduct && (
              <Box className="product-details">
                <Typography variant="h2" className="product-name">
                  {scannedProduct.productName}
                </Typography>
                <Typography variant="body1" className="product-field">
                  Company: {scannedProduct.manufacturer}
                </Typography>
                {scannedProduct.screwSize && (
                  <Typography variant="body1" className="product-field">
                    Screw Size: {scannedProduct.screwSize}
                  </Typography>
                )}
                <Typography variant="body1" className="product-field">
                  Device ID: {scannedProduct.deviceId}
                </Typography>
                {scannedProduct.lotNumber && (
                  <Typography variant="body1" className="product-field">
                    Lot/Batch: {scannedProduct.lotNumber}
                  </Typography>
                )}
                {scannedProduct.expirationDate && (
                  <Typography variant="body1" className="product-field">
                    Expires: {scannedProduct.expirationDate}
                  </Typography>
                )}
              </Box>
            )}

            <Box className="quantity-selector">
              <Typography variant="subtitle2">Quantity:</Typography>
              <TextField
                type="number"
                value={quantity}
                onChange={(e) =>
                  handleQuantityChange(parseInt(e.target.value) || 1)
                }
                inputProps={{ min: 1 }}
                size="small"
                className="quantity-input"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="body1" className="next-step-instruction">
        Now load this product into the tray, then capture an image to confirm
        the placement.
      </Typography>

      <Box className="action-buttons">
        <Button
          variant="outlined"
          onClick={handleBackToScanning}
          className="back-btn"
        >
          Scan Different Product
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleProceedToCamera}
          className="proceed-btn"
          startIcon={<CameraAlt />}
        >
          Capture Tray Image
        </LoadingButton>
      </Box>
    </>
  )

  const renderCameraMode = () => (
    <div className="camera-mode">
      <div className="mode-header">
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToScanning}
          className="back-button"
        >
          Back to{' '}
          {scanMode === 'continuous'
            ? 'Continuous Scanning'
            : 'Product Details'}
        </Button>
        <div className="header-content">
          <Typography variant="h6" className="mode-title">
            Capture Tray Image
          </Typography>
          <Typography variant="body2" className="mode-subtitle">
            Load all hardware first, then capture tray image
          </Typography>
        </div>
      </div>

      <div className="camera-section">
        <div className="camera-container">
          {isCameraMenuOpen && (
            <div className="camera-options">
              {cameras.map((camera) => (
                <div
                  key={camera.deviceId}
                  className={`camera-option ${
                    camera.deviceId === activeCamera?.deviceId ? 'active' : ''
                  }`}
                  onClick={() => handleSelectCamera(camera)}
                >
                  <Typography className="label">{camera.label}</Typography>
                  <Typography className="type">{camera?.cameraType}</Typography>
                </div>
              ))}
            </div>
          )}

          <LoadingButton
            variant="contained"
            loading={false}
            onClick={handleToggleCameraMenu}
            startIcon={<FlipCameraIos />}
            className="camera-switch"
          >
            Switch Camera
          </LoadingButton>

          {image ? (
            <div className="image-container">
              <img src={image} alt="Captured Tray" className="captured-image" />
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="camera-feed"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                backgroundColor: '#000',
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(() => {
                    // Ignore play promise rejection
                  })
                }
              }}
            />
          )}

          {!image && (
            <div className="capture-buttons">
              <LoadingButton
                className="capture"
                onClick={() => handleImageCapture(true)}
                loading={false}
                variant="text"
              >
                <CameraAlt className="icon" />
              </LoadingButton>
            </div>
          )}
        </div>

        {image && (
          <div className="action-buttons">
            <Button
              onClick={handleRetakeImage}
              className="retake-btn"
              variant="outlined"
            >
              <RestartAlt sx={{ mr: 0.5 }} />
              Retake
            </Button>
            <LoadingButton
              onClick={handleConfirmImage}
              className="confirm-btn"
              variant="contained"
              loading={false}
            >
              Process Tray Image
            </LoadingButton>
          </div>
        )}
      </div>
    </div>
  )

  const renderHandCheckingMode = () => (
    <>
      <Box className="modal-header">
        <Typography variant="h5">Checking Image Quality</Typography>
      </Box>

      <Box className="processing-container">
        <CircularProgress size={64} color="primary" />
        <Typography variant="h6" className="processing-title">
          Validating Image
        </Typography>
        <Typography variant="body1" className="processing-description">
          Checking for hands and image quality...
        </Typography>

        {scanMode === 'continuous' ? (
          <Box className="processing-product-info">
            <Typography variant="body2" className="processing-product">
              Loading:{' '}
              {continuousScannedProducts.filter((p) => !p.error).length} product
              types (Total Qty: {quantity})
            </Typography>
          </Box>
        ) : (
          scannedProduct && (
            <Box className="processing-product-info">
              <Typography variant="body2" className="processing-product">
                Loading: {scannedProduct.productName} (Qty: {quantity})
              </Typography>
            </Box>
          )
        )}
      </Box>
    </>
  )

  const renderProcessingMode = () => (
    <>
      <Box className="modal-header">
        <Typography variant="h5">Processing Tray Image</Typography>
      </Box>

      <Box className="processing-container">
        <CircularProgress size={64} color="primary" />
        <Typography variant="h6" className="processing-title">
          Analyzing Tray Contents
        </Typography>
        <Typography variant="body1" className="processing-description">
          Processing image with screw detection API...
        </Typography>

        {scanMode === 'continuous' ? (
          <Box className="processing-product-info">
            <Typography variant="body2" className="processing-product">
              Loading:{' '}
              {continuousScannedProducts.filter((p) => !p.error).length} product
              types (Total Qty: {quantity})
            </Typography>
          </Box>
        ) : (
          scannedProduct && (
            <Box className="processing-product-info">
              <Typography variant="body2" className="processing-product">
                Loading: {scannedProduct.productName} (Qty: {quantity})
              </Typography>
            </Box>
          )
        )}
      </Box>
    </>
  )

  const renderContent = () => {
    switch (currentMode) {
      case 'scanning':
        return renderScanningMode()
      case 'continuous-scanning':
        return renderContinuousScanningMode()
      case 'product-details':
        return renderProductDetailsMode()
      case 'camera':
        return renderCameraMode()
      case 'hand-checking':
        return renderHandCheckingMode()
      case 'processing':
        return renderProcessingMode()
      default:
        return renderScanningMode()
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={`udi-scanner-modal ${currentMode}-mode ${
        scanMode === 'continuous' && currentMode === 'continuous-scanning'
          ? 'continuous-mode'
          : ''
      }`}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <Box className="udi-scanner-modal-container">{renderContent()}</Box>
    </Modal>
  )
}

export default UDIScannerModal
